import { isPlatformServer, NgIf } from '@angular/common';
import { Component, Inject, Input, OnChanges, OnDestroy, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Geolocation } from '@capacitor/geolocation';
import { ConnectionStatus } from '@capacitor/network';
import { DEFAULT_MAP_LATITUDE, DEFAULT_MAP_LONGITUDE, IAutocompleteSuggestion, ILatLon } from '@curbnturf/entities';
import {
  formatAddressFormValue,
  formatCoordinatesFormValue,
  IAddressForm,
  ICoordinatesForm,
} from '@curbnturf/form-helpers';
import { AddressHelper, CoordinatesMapComponent, GeocodingService } from '@curbnturf/map';
import { MapModalComponent } from '@curbnturf/map/src/lib/map-modal/map-modal.component';
import { PlatformService } from '@curbnturf/network/src/lib/platform/platform.service';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@curbnturf/ng-bootstrap/src/lib/typeahead/typeahead';
import { StatusFacade } from '@curbnturf/status';
import { AlertController, IonicModule, LoadingController, ModalController } from '@ionic/angular';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';

@Component({
  standalone: true,
  selector: 'curbnturf-location',
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss'],
  imports: [CoordinatesMapComponent, IonicModule, MapModalComponent, NgbTypeahead, NgIf, ReactiveFormsModule],
})
export class LocationComponent implements OnChanges, OnDestroy {
  @Input() addressForm: FormGroup<IAddressForm>;
  @Input() coordinatesForm: FormGroup<ICoordinatesForm>;
  @Input() coordinatesShow: boolean;

  private simpleViewToggle = false;
  typeaheadLoading = false;
  minStringLength = 6;
  geocodingAutocompleteResultsSubject: Subject<IAutocompleteSuggestion[]>;
  geocodingAutocompleteResults$: Observable<IAutocompleteSuggestion[]>;
  searchField = new FormControl('');
  pointListSubject: Subject<ILatLon[]>;
  pointList$: Observable<ILatLon[]>;
  connectionStatus: ConnectionStatus;

  latitudeError: string | undefined;
  longitudeError: string | undefined;

  private subscriptions: Subscription[] = [];

  get showMapOnMobile(): boolean {
    return (
      !this.platformService.isPhysicalDevice() ||
      (this.platformService.isPhysicalDevice() && this.connectionStatus?.connected)
    );
  }

  get simpleView(): boolean {
    return this.simpleViewToggle || !this.showMapOnMobile;
  }

  get latitude() {
    if (this.coordinatesForm) {
      return this.coordinatesForm.get('lat')?.value;
    }

    return;
  }

  get longitude() {
    if (this.coordinatesForm) {
      return this.coordinatesForm.get('lon')?.value;
    }

    return;
  }

  constructor(
    private alertController: AlertController,
    private geocoding: GeocodingService,
    private loadingCtrl: LoadingController,
    private modalController: ModalController,
    private platformService: PlatformService,
    private statusFacade: StatusFacade,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
    this.geocodingAutocompleteResultsSubject = new Subject();
    this.geocodingAutocompleteResults$ = this.geocodingAutocompleteResultsSubject.asObservable();

    this.pointListSubject = new BehaviorSubject([]);
    this.pointList$ = this.pointListSubject.asObservable();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.addressForm && changes.addressForm.currentValue) {
      this.addressForm.valueChanges.subscribe(() => this.updateFromForm());

      // update even if no form changes
      this.updateFromForm();
    }

    if (changes.coordinatesForm.currentValue) {
      this.subscriptions.push(
        this.statusFacade.networkStatus$.subscribe((status) => {
          this.connectionStatus = status;
        }),
        this.coordinatesForm.valueChanges.subscribe(() => this.coordinatesChanged()),
      );

      this.coordinatesChanged();
      this.checkCoordinatesForSimpleView();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  async setCoordinatesToCurrent() {
    const loading = await this.loadingCtrl.create({
      message: 'Finding your precise location...',
      duration: 30000,
    });

    await loading.present();

    const location = await Geolocation.getCurrentPosition();
    await loading.dismiss();

    this.setCoordinates({
      lat: location.coords.latitude,
      lon: location.coords.longitude,
    });
  }

  async setCoordinatesFromAddress(modalId?: string) {
    if (!this.showMapOnMobile) {
      const alert = await this.alertController.create({
        header: 'You are offline.',
        message: 'Map feature is not available while offline.',
        buttons: [
          {
            text: 'Okay',
          },
        ],
      });

      await alert.present();
      return;
    }
    if (this.coordinatesForm) {
      const address = this.addressForm.value;

      if (address.address && address.city && address.state) {
        this.subscriptions.push(
          this.geocoding
            .fetch(`${address.address} ${address.city} ${address.state} ${address.zipcode}`)
            .subscribe((location: ILatLon) => {
              this.setCoordinates(location);
              if (modalId) {
                this.getCoordinatesFromMap();
              }
            }),
        );
      } else {
        let lat;
        let lon;
        try {
          const location = await Geolocation.getCurrentPosition();
          lat = location.coords.latitude;
          lon = location.coords.longitude;
        } catch (e) {
          lat = DEFAULT_MAP_LATITUDE;
          lon = DEFAULT_MAP_LONGITUDE;
        }
        this.setCoordinates({ lat, lon });
        if (modalId) {
          await this.getCoordinatesFromMap();
        }
      }
    }
  }

  async getCoordinatesFromMap() {
    if (!this.showMapOnMobile) {
      const alert = await this.alertController.create({
        header: 'You are offline.',
        message: 'Map feature is not available while offline.',
        buttons: [
          {
            text: 'Okay',
          },
        ],
      });

      await alert.present();
      return;
    }
    const modal = await this.modalController.create({
      component: MapModalComponent,
      componentProps: {
        pins$: this.pointList$,
        locationClicked: (coordinates: ILatLon) => this.locationClicked(coordinates),
      },
    });
    await modal.present();
  }

  locationClicked(coordinates: ILatLon) {
    this.setCoordinates(coordinates);
  }

  // for typeahead
  autocompleteUpdate(str: string) {
    if (str.length >= this.minStringLength) {
      this.geocoding
        .autocomplete(str)
        .pipe(
          map((response) =>
            response.items.map((suggestion: IAutocompleteSuggestion) => ({
              ...suggestion,
              label: AddressHelper.suggestionToString(suggestion),
            })),
          ),
        )
        .subscribe((suggestions) => this.geocodingAutocompleteResultsSubject.next(suggestions));
    }
  }

  searchPoi = (text$: Observable<string>) => {
    return text$.pipe(
      debounceTime(isPlatformServer(this.platformId) ? 0 : 300),
      distinctUntilChanged(),
      switchMap((term) => {
        this.autocompleteUpdate(term);

        if (term.length < this.minStringLength) {
          return of([]);
        }

        return this.geocodingAutocompleteResults$.pipe(
          take(2),
          catchError(() => {
            return of([]);
          }),
        );
      }),
    );
  };

  poiFormatter(result: IAutocompleteSuggestion): string {
    return result.address?.label;
  }

  locationSelected(typeahead: NgbTypeaheadSelectItemEvent, modalId?: string) {
    this.setAddressFromAutocomplete(typeahead.item);
    this.simpleViewToggle = false;
    if (this.coordinatesForm) {
      this.setCoordinatesFromAddress(modalId);
    }
  }

  private setAddressFromAutocomplete(suggestion: IAutocompleteSuggestion) {
    this.addressForm.setValue(AddressHelper.suggestionToAddress(suggestion));
    this.addressForm.markAsDirty();
  }

  private updateFromForm() {
    const address = formatAddressFormValue(this.addressForm);

    if (this.searchField && address) {
      this.searchField.setValue(AddressHelper.addressToString(address));
      this.searchField.markAsDirty();
    }
  }
  // end typeahead

  private setCoordinates(coordinates: ILatLon) {
    if (coordinates.lat && coordinates.lon) {
      this.coordinatesForm.setValue(coordinates);
      this.coordinatesForm.markAsDirty();

      // this.pointListSubject.next([coordinates]);
    }
  }

  private coordinatesChanged(): void {
    const coordinates = formatCoordinatesFormValue(this.coordinatesForm);
    if (coordinates) {
      this.pointListSubject.next([coordinates]);
    }

    this.checkCoordinatesForSimpleView();
  }

  private checkCoordinatesForSimpleView(): void {
    this.simpleViewToggle =
      (!this.addressForm ||
        !this.addressForm.value ||
        (!this.addressForm.value.address &&
          !this.addressForm.value.city &&
          !this.addressForm.value.state &&
          !this.addressForm.value.zipcode)) &&
      (!this.coordinatesForm.value || (!this.coordinatesForm.value.lat && !this.coordinatesForm.value.lon));
  }

  toggleSimpleView(toggle?: boolean) {
    if (toggle) {
      this.simpleViewToggle = toggle;
    } else {
      this.simpleViewToggle = !this.simpleViewToggle;
    }
  }

  validate() {
    if (this.latitude === undefined || this.latitude === null || this.latitude > 90 || this.latitude < -90) {
      this.latitudeError = 'Enter Latitude Value';
    } else {
      this.latitudeError = undefined;
    }
    if (this.longitude === undefined || this.longitude === null || this.longitude > 180 || this.longitude < -180) {
      this.longitudeError = 'Enter Longitude Value';
    } else {
      this.longitudeError = undefined;
    }
  }
}
